/**
 * Centralised axios client for the conveyancer portal (M5).
 *
 * One axios instance, used everywhere. Replaces the ad-hoc
 * `axios.get(...)` / `axios.post(...)` calls scattered through
 * AuthContext, the four pages, and the @bc/ui auth client.
 *
 * What this gives us:
 *
 *   1. **Token injection.** A request interceptor reads the bearer
 *      token from `localStorage` on every call, so we never have to
 *      remember to set the Authorization header at the call site.
 *
 *   2. **401 → logout.** A response interceptor catches 401 from any
 *      authenticated request, clears the token, and dispatches a
 *      window event so AuthContext can react and redirect to /login.
 *      We use a window event rather than importing AuthContext here
 *      to avoid the circular import (AuthContext imports api, api
 *      can't import AuthContext).
 *
 *   3. **X-Request-ID propagation.** Every outbound request gets a
 *      fresh UUID in the `X-Request-ID` header so server logs can be
 *      correlated with the browser-side action that triggered them.
 *      The bc_shared.errors envelope echoes this id back as
 *      `error.request_id`, which the page can show in error toasts.
 *
 *   4. **Single base URL.** Read once from `REACT_APP_BACKEND_URL` at
 *      module load. If the env var changes, restart the dev server —
 *      we don't dynamically re-read it.
 *
 * Usage:
 *
 *   import api from '@/services/api';
 *
 *   const sessions = await api.get('/sessions');
 *   const newBid  = await api.post(`/sessions/${id}/bid`, { discount_percentage: 15 });
 *
 * Note that paths are relative to `/api` — the base URL already
 * includes that prefix. Don't write `api.get('/api/sessions')`.
 */

import axios from "axios";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
if (!BACKEND_URL) {
  // Fail loud at module load — better than every fetch silently 404'ing
  // against the wrong host. The dev script's bootstrap.sh ensures this
  // is set in the .env file before yarn start.
  // eslint-disable-next-line no-console
  console.error(
    "REACT_APP_BACKEND_URL is not set. Check apps/conveyancer-portal/frontend/.env",
  );
}

const API_BASE = `${BACKEND_URL || ""}/api`;

const api = axios.create({
  baseURL: API_BASE,
  timeout: 30_000,
  headers: { "Content-Type": "application/json" },
});

// -----------------------------------------------------------------------------
// Request interceptor — token + correlation id
// -----------------------------------------------------------------------------
api.interceptors.request.use((config) => {
  const token = localStorage.getItem("token");
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  // Generate a fresh correlation id per request. crypto.randomUUID is
  // available in every browser back to 2022 and in jsdom (so the test
  // tier doesn't choke).
  if (typeof crypto !== "undefined" && typeof crypto.randomUUID === "function") {
    config.headers["X-Request-ID"] = crypto.randomUUID();
  }
  return config;
});

// -----------------------------------------------------------------------------
// Response interceptor — 401 → logout
// -----------------------------------------------------------------------------
//
// We dispatch a `bc:auth:logout` window event rather than importing
// AuthContext (which would create a circular import). AuthContext
// listens for it and clears its in-memory user state.
api.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error?.response?.status === 401) {
      const wasAuthenticated = !!localStorage.getItem("token");
      if (wasAuthenticated) {
        localStorage.removeItem("token");
        delete api.defaults.headers.common.Authorization;
        if (typeof window !== "undefined") {
          window.dispatchEvent(new CustomEvent("bc:auth:logout"));
        }
      }
    }
    return Promise.reject(error);
  },
);

/**
 * Extract a human-readable error message from an axios error.
 *
 * Handles three response shapes:
 *   1. Standard bc_shared.errors envelope: `{error: {code, message, request_id}}`
 *   2. Legacy FastAPI shape: `{detail: "..."}`
 *   3. Network / timeout errors: no response at all
 */
export function errorMessage(error) {
  if (!error) return "Unknown error";
  const data = error?.response?.data;
  if (data && typeof data === "object") {
    if (data.error && typeof data.error === "object" && data.error.message) {
      return String(data.error.message);
    }
    if (data.detail) return String(data.detail);
  }
  if (error.message) return String(error.message);
  return "Unknown error";
}

/**
 * Extract the structured error code from an axios error.
 * Returns an empty string if not present.
 */
export function errorCode(error) {
  const data = error?.response?.data;
  if (data && typeof data === "object" && data.error && typeof data.error === "object") {
    return String(data.error.code || "");
  }
  return "";
}

/**
 * Extract per-field validation errors from a 422 response.
 *
 * The bc_shared error envelope adds an `error.fields` array of
 * `{field, message}` on validation errors. This flattens it into a
 * `{ [fieldName]: message }` map the form can use to highlight inputs.
 * Model-level errors (no single field) are keyed under `__root__`.
 * Returns `{}` when there are no field errors.
 */
export function fieldErrors(error) {
  const fields = error?.response?.data?.error?.fields;
  if (!Array.isArray(fields)) return {};
  const out = {};
  for (const f of fields) {
    if (!f || typeof f !== "object") continue;
    const key = f.field || "__root__";
    if (!out[key]) out[key] = String(f.message || "Invalid value");
  }
  return out;
}

/**
 * Upload a file via multipart/form-data to /uploads/document.
 * Returns the response data: { url, filename, size }.
 */
export async function uploadDocument(file) {
  const form = new FormData();
  form.append("file", file);
  const res = await api.post("/uploads/document", form, {
    headers: { "Content-Type": "multipart/form-data" },
    timeout: 60_000,
  });
  return res.data;
}

export default api;
